import { useMemo } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useResolveIdentitiesByPhoneQuery } from '@frontend/api-comm-phone-number';
import { PersonsV3 } from '@frontend/api-person';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';

export type DisplayIdentity = {
  firstName: string;
  lastName: string;
  preferredName?: string;
  fullName: string;
  isLead: boolean;
};

export type UseThreadContactIdentityArgs = {
  orgId?: string;
  phoneNumber: string;
  locationId: string;
  isLead?: boolean;
  enabled?: boolean;
};

export type UseThreadContactIdentityResults = {
  isLoading: boolean;
  associatedContacts: Person[];
  primaryContactId: string;
  primaryContact?: Person;
  isLead: boolean;
  displayIdentity?: DisplayIdentity;
  isDefaultPrimaryContact?: boolean;
  refetch: (queryType?: 'associatedContacts' | 'primaryContact' | 'both') => Promise<{
    associatedContacts?: Person[];
    primaryContactId?: string;
  }>;
};

/**
 * Hook to resolve contact identity for a thread.
 * Handles both saved contacts (via useAssociatedContacts) and unsaved leads (via useResolveIdentitiesByPhoneQuery).
 *
 * Scenarios:
 * 1. Saved contact (associatedContacts or primaryContactId found) -> Use saved contact data.
 * 2. Unsaved lead (isLead = true, no associated contacts, lead identity found) -> Use lead firstName + lastName.
 * 3. Unsaved lead not found (isLead = true, no contacts, lead identity empty/error) -> Fall back to empty contact.
 * 4. Non-lead (isLead = false/undefined) -> Use saved contact data directly without querying lead identities.
 */
export const useThreadContactIdentity = ({
  phoneNumber,
  locationId,
  orgId,
  isLead = false,
  enabled = true,
}: UseThreadContactIdentityArgs): UseThreadContactIdentityResults => {
  const {
    isLoading: associatedContactsIsLoading,
    associatedContacts,
    primaryContactId: savedPrimaryContactId,
    isDefaultPrimaryContact,
    refetch: refetchAssociatedContacts,
  } = PersonsV3.PersonHooks.useAssociatedContacts({
    phoneNumber,
    locationId,
    enabled: enabled && !!locationId && !!phoneNumber,
  });

  const hasSavedContacts = associatedContacts.length > 0 || !!savedPrimaryContactId;

  // Only attempt lead identity resolution when the thread is flagged as a lead
  // AND associated contacts resolution has completed with no saved contacts found.
  const shouldResolveLeadIdentity =
    enabled && !!isLead && !!locationId && !!phoneNumber && !associatedContactsIsLoading && !hasSavedContacts;

  const formattedPhoneNumber = useMemo(() => formatPhoneNumberE164(phoneNumber) || phoneNumber, [phoneNumber]);

  const {
    data: identityResponse,
    isLoading: isLeadQueryLoading,
    refetch: refetchLeadQuery,
  } = useResolveIdentitiesByPhoneQuery(
    {
      lookups: [{ locationId, phoneNumber: formattedPhoneNumber }],
      orgId,
    },
    {
      enabled: shouldResolveLeadIdentity,
      staleTime: 5 * 60 * 1000,
    }
  );

  const resolvedLead = useMemo(() => {
    if (!shouldResolveLeadIdentity || !identityResponse?.identities?.length) {
      return undefined;
    }
    const matchingIdentity = identityResponse.identities.find((identity) => 'lead' in identity && identity.lead);
    if (!matchingIdentity || !('lead' in matchingIdentity) || !matchingIdentity.lead) {
      return undefined;
    }
    const lead = matchingIdentity.lead;
    if (!lead.firstName && !lead.lastName) {
      return undefined;
    }
    return lead;
  }, [shouldResolveLeadIdentity, identityResponse]);

  const leadContact = useMemo<Person | undefined>(() => {
    if (!resolvedLead) return undefined;

    return {
      personId: '',
      sourceTenantId: '',
      personPmid: '',
      householdId: '',
      householdPmid: '',
      isGuardian: false,
      createdAt: resolvedLead.submittedAt || '',
      modifiedAt: resolvedLead.submittedAt || '',
      firstName: resolvedLead.firstName || '',
      lastName: resolvedLead.lastName || '',
      // Explicitly no preferredName for leads
      preferredName: '',
    };
  }, [resolvedLead]);

  const savedPrimaryContact = useMemo(() => {
    if (!savedPrimaryContactId && associatedContacts.length === 0) return undefined;
    return associatedContacts.find((c) => c.personId === savedPrimaryContactId) || associatedContacts[0];
  }, [associatedContacts, savedPrimaryContactId]);

  const primaryContact = hasSavedContacts ? savedPrimaryContact : leadContact;
  const isResolvedLead = !hasSavedContacts && !!resolvedLead;
  const primaryContactId = hasSavedContacts ? savedPrimaryContactId : '';

  const displayIdentity = useMemo<DisplayIdentity | undefined>(() => {
    if (primaryContact) {
      const firstName = primaryContact.firstName || '';
      const lastName = primaryContact.lastName || '';
      const preferredName = primaryContact.preferredName;
      const displayFirst = !isResolvedLead && preferredName ? preferredName : firstName;
      const fullName = [displayFirst, lastName].filter(Boolean).join(' ');

      return {
        firstName,
        lastName,
        preferredName: isResolvedLead ? undefined : preferredName,
        fullName,
        isLead: isResolvedLead,
      };
    }
    return undefined;
  }, [primaryContact, isResolvedLead]);

  const refetch: UseThreadContactIdentityResults['refetch'] = async (queryType = 'both') => {
    if (shouldResolveLeadIdentity) {
      await refetchLeadQuery();
    }
    return refetchAssociatedContacts(queryType);
  };

  const isLoading = enabled && (associatedContactsIsLoading || (shouldResolveLeadIdentity && isLeadQueryLoading));

  return enabled
    ? {
        isLoading,
        associatedContacts,
        primaryContactId,
        primaryContact,
        isLead: isResolvedLead,
        displayIdentity,
        isDefaultPrimaryContact: hasSavedContacts ? isDefaultPrimaryContact : undefined,
        refetch,
      }
    : {
        isLoading: false,
        associatedContacts: [],
        primaryContactId: '',
        primaryContact: undefined,
        isLead: false,
        displayIdentity: undefined,
        isDefaultPrimaryContact: undefined,
        refetch: () => Promise.resolve({}),
      };
};
